import { isNotDefined } from '@sgme/fp';
import { useEffect, useState } from 'react';

import { SECOND_IN_MILLIS, type TimeInSecond } from '@/utils/time.ts';

/**
 * Hook to manage and countdown remaining time in seconds.
 *
 * @example
 * ```tsx
 * const { rfsRemainingTime } = useAppSelector(/* selector to get remaining time from RFS *\/);
 * const remainingTime = useRemainingTime(rfsRemainingTime);
 * ```
 *
 * @param remainingTimeInSeconds - Initial remaining time in seconds or external update.
 * @returns The updated remaining time in seconds.
 */
export function useRemainingTime(remainingTimeInSeconds: TimeInSecond | undefined) {
  const [remainingTime, setRemainingTime] = useState<TimeInSecond | undefined>(remainingTimeInSeconds);

  useEffect(() => {
    setRemainingTime(remainingTimeInSeconds);
  }, [remainingTimeInSeconds]);

  useEffect(() => {
    if (isNotDefined(remainingTime) || remainingTime <= 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setRemainingTime((previousRemainingTime) => {
        if (isNotDefined(previousRemainingTime)) {
          return undefined;
        }

        return previousRemainingTime > 0 ? previousRemainingTime - 1 : 0;
      });
    }, SECOND_IN_MILLIS);

    return () => {
      clearInterval(intervalId);
    };
  }, [remainingTime]);

  return remainingTime;
}
